// src/components/admin/UberOrdersPanel.jsx
import {
  AlertTriangle,
  Calendar,
  Clock,
  MapPin,
  Package,
  RefreshCw,
  Truck,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useConfig } from "../../context/ConfigContext";
import { useNotification } from "../../context/NotificationContext";
import axios from "../../utils/axiosHandler";

const UberOrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showNotification, playNotificationSound } = useNotification();
  const { config } = useConfig();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(60); // seconds

  useEffect(() => {
    fetchUberOrders();

    // Set up automatic refresh
    const intervalId = setInterval(fetchUberOrders, refreshInterval * 1000);

    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, [refreshInterval]);

  // Set up a socket listener for new Uber orders
  useEffect(() => {
    // Listen for the 'new-uber-order' event from the socket
    const handleNewUberOrder = (orderData) => {
      // Play sound notification
      playNotificationSound();

      // Show notification
      showNotification(
        "info",
        `New Uber Eats order #${orderData.id} received!`
      );

      // Update orders list (add to beginning)
      setOrders((prevOrders) => [orderData, ...prevOrders]);
    };

    // Register global event listener
    window.addEventListener("new-uber-order", handleNewUberOrder);

    // Clean up
    return () => {
      window.removeEventListener("new-uber-order", handleNewUberOrder);
    };
  }, []);

  const fetchUberOrders = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get("/api/admin/uber-orders");
      setOrders(data.orders);
      setError(null);
    } catch (err) {
      console.error("Error fetching Uber orders:", err);
      setError("Unable to load Uber orders. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchUberOrders();
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order._id === selectedOrder ? null : order._id);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    let color = "";
    let bgColor = "";

    switch (status) {
      case "COMPLETED":
      case "DELIVERED":
        color = "#00694B";
        bgColor = "#DDFCF0";
        break;
      case "PICKED_UP":
      case "DELIVERY_IN_PROGRESS":
        color = "#A05700";
        bgColor = "#FFF3E0";
        break;
      case "ACCEPTED":
      case "DISPATCHED":
        color = "#0057B7";
        bgColor = "#E3F2FD";
        break;
      case "FAILED":
      case "CANCELED":
        color = "#B71C1C";
        bgColor = "#FFEBEE";
        break;
      default:
        color = "#424242";
        bgColor = "#F5F5F5";
    }

    return (
      <span
        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{ backgroundColor: bgColor, color: color }}
      >
        {status}
      </span>
    );
  };

  const renderOrderDetails = (order) => {
    if (selectedOrder !== order._id) return null;

    return (
      <div className="mt-3 pl-8 border-l-2 border-gray-200">
        <div className="p-4 bg-gray-50 rounded-md space-y-3">
          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-sm text-gray-500">Customer</p>
              <p className="font-medium">{order.customerName}</p>
            </div>
            {order.customerPhone && (
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="font-medium">{order.customerPhone}</p>
              </div>
            )}
          </div>

          {/* Delivery Address */}
          <div>
            <p className="text-sm text-gray-500 flex items-center">
              <MapPin className="w-4 h-4 mr-1" /> Delivery Address
            </p>
            <p className="font-medium">{order.deliveryAddress}</p>
          </div>

          {/* Order Items */}
          <div>
            <p className="text-sm text-gray-500 flex items-center">
              <Package className="w-4 h-4 mr-1" /> Items
            </p>
            <div className="mt-1">
              {order.items.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between py-1 border-b border-gray-100"
                >
                  <span className="text-sm">
                    {item.quantity}x {item.name}
                  </span>
                  <span className="text-sm font-medium">
                    ${item.price.toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Courier Info if available */}
          {order.courier && (
            <div>
              <p className="text-sm text-gray-500 flex items-center">
                <Truck className="w-4 h-4 mr-1" /> Courier
              </p>
              <div className="flex items-center mt-1">
                {order.courier.imageUrl ? (
                  <img
                    src={order.courier.imageUrl}
                    alt="Courier"
                    className="w-8 h-8 rounded-full mr-2"
                    onError={(e) =>
                      (e.target.src = "/images/default-courier.png")
                    }
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-200 rounded-full mr-2 flex items-center justify-center">
                    <Truck className="w-4 h-4 text-gray-400" />
                  </div>
                )}
                <span className="text-sm font-medium">
                  {order.courier.name || "Assigned Courier"}
                </span>
              </div>
            </div>
          )}

          {/* Order Totals */}
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex justify-between">
              <span className="text-sm">Subtotal</span>
              <span className="text-sm">${order.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">Delivery Fee</span>
              <span className="text-sm">${order.deliveryFee.toFixed(2)}</span>
            </div>
            {order.taxes > 0 && (
              <div className="flex justify-between">
                <span className="text-sm">Tax</span>
                <span className="text-sm">${order.taxes.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-medium mt-1 pt-1 border-t border-gray-200">
              <span>Total</span>
              <span>${order.total.toFixed(2)}</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-2 mt-4">
            <button
              className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
              onClick={() =>
                window.open(`/admin/orders/${order.orderId}`, "_blank")
              }
            >
              View Full Order
            </button>
            {order.trackingUrl && (
              <button
                className="px-3 py-1.5 text-sm rounded text-white"
                style={{ backgroundColor: config.colors.primary }}
                onClick={() => window.open(order.trackingUrl, "_blank")}
              >
                Open Tracker
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading && orders.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Uber Eats Orders
          </h2>
        </div>
        <div className="flex justify-center items-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">
          Uber Eats Orders
        </h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <span className="text-sm text-gray-500 mr-2">Auto refresh:</span>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              className="text-sm border rounded px-2 py-1"
            >
              <option value={30}>30 seconds</option>
              <option value={60}>1 minute</option>
              <option value={300}>5 minutes</option>
            </select>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 rounded-full hover:bg-gray-100"
            title="Refresh Now"
          >
            <RefreshCw className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {orders.length === 0 ? (
        <div className="text-center p-12 bg-gray-50 rounded-md">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">
            No Uber orders yet
          </h3>
          <p className="text-gray-500 mt-2">
            When customers place delivery orders, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => (
            <div
              key={order._id}
              className="border rounded-md overflow-hidden hover:shadow-md transition-shadow"
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => handleOrderClick(order)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center space-x-2">
                      <h3 className="font-medium">
                        Order #{order.orderNumber}
                      </h3>
                      {getStatusBadge(order.status)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {formatDate(order.createdAt)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium">${order.total.toFixed(2)}</div>
                    <div className="text-sm text-gray-500 mt-1 flex items-center justify-end">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>
                        ETA: {order.estimatedDeliveryTime || "Calculating..."}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {renderOrderDetails(order)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UberOrdersPanel;
